/// <reference types="datatables.net" />

import { ProductId, Serializer } from "../model/datasource";

export interface CustomDataTables {
	column(index: number): CustomDataTables;
	search(text: string, regex?: boolean): CustomDataTables;
	cache(key: string): CustomDataTables;
	sort(): CustomDataTables;
	draw(): void;
	table(index: number | undefined): CustomDataTables;
	header(): HTMLElement;
	unique(): string[];
}

export default class OverviewSummaryTable<P extends ProductId> {
	jQuery: JQueryStatic;
	productIdSerializer: Serializer<P>;

	constructor(jQuery: JQueryStatic, product: Serializer<P>) {
		this.jQuery = jQuery;
		this.productIdSerializer = product;
	}

	display(element: HTMLElement, product: P): CustomDataTables {
		const dataTable: CustomDataTables = this.table(element, product);
		const filterRow = this.addFilterRow(dataTable);
		const toggle = this.addToggle(filterRow);
		return dataTable;
	}

	table(element: HTMLElement, product: P): any {
		return this.jQuery(element).DataTable({
			dom: "<'row'<'col-sm-12 col-md-6 filter-toggle'><'col-sm-12 col-md-6 d-flex flex-row justify-content-end'l>>" +
				"<'row'<'col-sm-12'tr>>" +
				"<'row'<'col-sm-12 col-md-5'i><'col-sm-12 col-md-7'p>>",
			responsive: true,
			order: [[ 2, 'desc' ]],
			columns: [
				{ visible: false, type: 'num' },
				{ render: (data, type, row, meta) => {
						if (type === 'display') {
							const complete = row[1] === 'true';
							const first = (this.jQuery(element).DataTable()
								.cell({row: meta.row, column: meta.col})
								.node() as HTMLElement)
								.dataset.first;
							return `<div class="d-flex flex-row justify-content-center">
								<div style="width: 4.5em;">
									<input type="checkbox"
										${data === 'true' ? 'checked' : ''}
										onclick="return false;"
										style="filter: hue-rotate(270deg);"
									/>
									<a class="small ml-1" href="${[
										'/practicequestion',
										(complete ? 'result': 'test'),
										this.productIdSerializer.toString(product),
										row[0]
									].join('/') + (first && !complete ? `#${first}` : '')}">
										<u>${(row[1] === 'true' ? 'Review' : 'Resume')}</u>
									</a>
								</div>
							</div>`;
						}
						return data;
					} },
				{ type: 'date', render: (data, type, row, meta) => {
						if (type === 'display') {
							return new Date(data).toLocaleString();
						}
						return data;
					} },
				{ type: 'html' },
				{ type: 'num', render: (data, type, row, meta) => {
						if (type === 'display') {
							return (this.jQuery(element).DataTable()
								.cell({row: meta.row, column: meta.col})
								.node() as HTMLElement)
								.dataset.formatted;
						}
						return data;
					} },
				{ type: 'num' },
				{ type: 'num' },
				{ type: 'num' },
				{ type: 'num' },
				{ type: 'num', render: (d) => d }
			]
		});
	}

	values(table: CustomDataTables, index: number): string[] {
		return this.jQuery.makeArray(table.column(index)
			.cache('search')
			.sort()
			.unique()
		);
	}

	search(table: CustomDataTables, index: number, value: string, regex: boolean): void {
		table.column(index)
			.search(value, regex)
			.draw();
	}

	addFilterRow(table: CustomDataTables): HTMLTableRowElement {
		const filterRow = document.createElement('tr');
		filterRow.classList.add('collapse', 'practice-filter');
		(table.table(undefined).header() as HTMLElement)
			.prepend(filterRow);
		[
			{ render: (index, cell) => {
					cell.innerHTML = `<select class="form-control-sm">
						<option value="">View All</option>
						${this.values(table, index).map(value =>
							`<option value="${value}">
								${value === 'true' ? 'Done': 'In Progress'}
							</option>`
						)}
					</select>`;
					const select = cell.querySelector('select');
					select.addEventListener('change', (e) => {
						this.search(table, index, e.target.value, false)
					});
				} },
			{ render: (index, cell) => {
					cell.innerHTML = `
						<input type="date" class="form-control-sm" name="from" />
						<div class="text-center">to</div>
						<input type="date" class="form-control-sm" name="to" />
					`;
					const dates = this.values(table, index);
					const from = cell.querySelector('input[name=from]');
					const to = cell.querySelector('input[name=to]');
					from.addEventListener('change', () =>
						this.dateSearch(table, dates, from, to, index)
					);
					to.addEventListener('change', () =>
						this.dateSearch(table, dates, from, to, index)
					);
				} },
			{ render: (index, cell) => {
					cell.innerHTML = `<select class="form-control-sm">
						<option value="">All Tests</optionv>
						${this.values(table, index).map(value =>
							`<option value="${value}">${value}</option>`
						)}
					</select>`;
					const select = cell.querySelector('select');
					select.addEventListener('change', (e) =>
						this.search(table, index,
							e.target.value ? `^${e.target.value}$` : '',
							true
						)
					);
				} },
			{},
			{},
			{},
			{},
			{},
			{}
		].forEach((params, i) => {
			const index = i + 1;
			const cell = document.createElement('td');
			cell.classList.add('align-bottom', 'border-top-0');
			if ('render' in params) {
				params.render(index, cell);
			}
			filterRow.append(cell);
		});
		return filterRow;
	}

	dateSearch (table: CustomDataTables, dates: string[], from: HTMLInputElement, to: HTMLInputElement, index: number): void {
		const match = dates.filter(d =>
			(!from.value || d >= from.value) &&
			(!to.value || d <= to.value)
		).map(d =>
			// Escape characters for regex
			d.replace(/[ +]/g, '.')
		);
		this.search(table, index,
			'(' + (match.length > 0 ?
				match.join('|') :
				'n/a'
			) + ')',
			true
		);
	};

	addToggle(filterRow: HTMLElement): HTMLButtonElement {
		const toggleCreate = document.createElement('temp');
		toggleCreate.innerHTML = `<button
			data-toggle="collapse"
			data-target=".practice-filter"
			title="Show Filters"
			class="btn btn-outline-secondary btn-sm"
		>
			<i class="fas fa-filter" />
		</button>`;
		const toggle = toggleCreate.querySelector<HTMLButtonElement>('button');
		document.querySelector('.filter-toggle').append(toggle);
		this.jQuery(filterRow).on('hide.bs.collapse', () => toggle.title = 'Show Filters');
		this.jQuery(filterRow).on('show.bs.collapse', () => toggle.title = 'Hide Filters');
		return toggle;
	}

}